import { useEffect, useState, useRef } from 'react';
import { appProfiler } from '../utils/appProfiler';

interface FPSMonitorProps {
  warningThreshold?: number;
}

export function FPSMonitor({ warningThreshold = 30 }: FPSMonitorProps) {
  const [fps, setFps] = useState<number>(60);
  const [showProfilerTable, setShowProfilerTable] = useState(false);
  const [profilerStats, setProfilerStats] = useState<any>({});
  const frameCount = useRef(0);
  const lastTime = useRef(performance.now());
  const lowFpsCount = useRef(0);

  useEffect(() => {
    let animationFrameId: number;

    const loop = () => {
      const now = performance.now();
      frameCount.current++;

      if (now >= lastTime.current + 1000) {
        const currentFps = Math.round((frameCount.current * 1000) / (now - lastTime.current));
        setFps(currentFps);

        if (currentFps < warningThreshold) {
          lowFpsCount.current++;
          if (lowFpsCount.current > 3) {
            lowFpsCount.current = 0;
          }
        } else {
          lowFpsCount.current = 0;
        }

        if (showProfilerTable) {
          setProfilerStats(appProfiler.getSummary().stats);
        }

        frameCount.current = 0;
        lastTime.current = now;
      }

      animationFrameId = requestAnimationFrame(loop);
    };

    animationFrameId = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [warningThreshold, showProfilerTable]);

  let colorClass = 'text-[#00ffa3]';
  if (fps < warningThreshold) {
    colorClass = 'text-[#ff3d67]';
  } else if (fps < 45) {
    colorClass = 'text-[#ffb800]';
  }

  return (
    <div className="fixed bottom-4 right-4 z-[9999] pointer-events-auto flex flex-col items-end gap-2 font-mono text-[10px]">
      {showProfilerTable && (
        <div className="bg-[#0a0e27]/95 backdrop-blur-2xl border border-[#00d9ff]/40 rounded-lg p-3 shadow-[0_0_30px_rgba(0,0,0,0.8)] text-[#e0e0e0] max-w-[360px] max-h-[300px] overflow-y-auto pointer-events-auto">
          <div className="flex justify-between items-center pb-2 mb-2 border-b border-[#00d9ff]/20">
            <span className="font-bold text-[#00d9ff] uppercase tracking-wider">Profiler Diagnostic</span>
            <button 
              onClick={() => appProfiler.printSummary()} 
              className="px-2 py-0.5 bg-[#00d9ff]/20 text-[#00d9ff] rounded hover:bg-[#00d9ff]/40 transition text-[9px]"
            >
              Log Console
            </button>
          </div>
          <table className="w-full text-left text-[9px] border-collapse">
            <thead>
              <tr className="text-[#888888] border-b border-white/10">
                <th className="py-1">Function/Block</th>
                <th className="py-1 text-right">Avg (ms)</th>
                <th className="py-1 text-right">Peak (ms)</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(profilerStats).map(([key, stat]: [string, any]) => (
                <tr key={key} className="border-b border-white/5 hover:bg-white/5">
                  <td className="py-1 pr-2 truncate max-w-[170px]" title={key}>{key}</td>
                  <td className={`py-1 text-right font-bold ${stat.avgMs > 10 ? 'text-[#ff3d67]' : stat.avgMs > 3 ? 'text-[#ffb800]' : 'text-[#00ffa3]'}`}>
                    {stat.avgMs}ms
                  </td>
                  <td className="py-1 text-right text-[#888888]">
                    {stat.peakMs}ms
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <button
        onClick={() => {
          setShowProfilerTable(!showProfilerTable);
          if (!showProfilerTable) setProfilerStats(appProfiler.getSummary().stats);
        }}
        className="bg-[#0a0e27]/80 backdrop-blur-xl border border-[#00d9ff]/30 hover:border-[#00d9ff] rounded px-3 py-1.5 flex items-center gap-2 uppercase shadow-[0_0_15px_rgba(0,217,255,0.1)] transition-all cursor-pointer"
        title="Clique para abrir o painel diagnóstico de latência"
      >
        <span className="text-[#666666] tracking-[2px]">FPS</span>
        <span className={`font-bold ${colorClass}`}>{fps}</span>
        <span className="text-[8px] text-[#00d9ff]/70 border border-[#00d9ff]/30 px-1 rounded">DIAG</span>
      </button>
    </div>
  );
}
